import React, { useEffect, useState } from 'react';
import { Image as ImageIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

/** Stored as a comma list of paths relative to uploads/ad-creatives. */
export const parseAdImages = (value) =>
  String(value || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

/**
 * Read back through the API, not the /uploads static mount: in production only
 * /api/* is routed to the backend, so /uploads/... is answered by the frontend
 * container with index.html and the image renders broken.
 */
export const adImageUrl = (relativePath) =>
  `${basePath}/api/paid-traffic/ad-images/${String(relativePath || '')
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`;

/**
 * "View ad" button plus the lightbox it opens.
 *
 * Replaces the Ad Library deep link. That link was a promise rather than a
 * record: it sends you to Meta, where the ad may since have been taken down,
 * edited, or hidden behind a country filter. A screenshot captured when the find
 * was logged still shows the creative after the campaign ends — which is the
 * whole point of keeping past-advertising records.
 *
 * Renders nothing when an entry has no screenshots, so Google finds and older
 * Meta finds are unaffected.
 */
const AdCreativeViewer = ({ images, label, compact = false }) => {
  const paths = Array.isArray(images) ? images : parseAdImages(images);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  // Arrow keys and Escape, because a lightbox that traps you is worse than no
  // lightbox. Bound only while open so it never competes with the page.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % paths.length);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + paths.length) % paths.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, paths.length]);

  if (paths.length === 0) return null;

  const openAt = (e) => {
    // The row underneath is itself clickable on the dashboard.
    e.preventDefault();
    e.stopPropagation();
    setIndex(0);
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openAt}
        title={`View the ad creative${paths.length > 1 ? ` (${paths.length} images)` : ''}`}
        className={`inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400 ${
          compact ? 'text-[11px]' : 'text-xs'
        }`}
      >
        <ImageIcon className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        View ad
        {paths.length > 1 && <span className="opacity-70">({paths.length})</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
          }}
        >
          <div
            className="relative max-h-full w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="truncate text-sm text-white/90" title={label}>
                {label}
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1 text-white/70 hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="relative flex items-center justify-center rounded-xl bg-white p-2 dark:bg-slate-900">
              <img
                src={adImageUrl(paths[index])}
                alt={`Ad creative ${index + 1} of ${paths.length}`}
                className="max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
              />

              {paths.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setIndex((i) => (i - 1 + paths.length) % paths.length)}
                    className="absolute left-2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setIndex((i) => (i + 1) % paths.length)}
                    className="absolute right-2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>

            {paths.length > 1 && (
              <p className="mt-2 text-center text-xs text-white/70">
                {index + 1} / {paths.length}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdCreativeViewer;
